import { DOCUMENT } from '@angular/common';
import { inject, Service } from '@angular/core';

interface GrecaptchaEnterprise {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare const window: Window & {
  grecaptcha?: { enterprise: GrecaptchaEnterprise };
};

const EXECUTE_TIMEOUT_MS = 15000;

@Service()
export class RecaptchaService {
  document = inject(DOCUMENT);

  scriptLoad?: Promise<void>;

  load = (siteKey: string) => {
    this.scriptLoad ??= new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');

      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
      script.onload = () => resolve();
      script.onerror = () => {
        script.remove();
        this.scriptLoad = undefined;
        reject(new Error('reCAPTCHA script failed to load'));
      };

      this.document.head.appendChild(script);
    });

    return this.scriptLoad;
  };

  execute = async (siteKey: string, action: string) => {
    await this.load(siteKey);

    const enterprise = window.grecaptcha?.enterprise;

    if (!enterprise) {
      throw new Error('reCAPTCHA is unavailable');
    }

    let timeout: ReturnType<typeof setTimeout> | undefined;

    const token = new Promise<string>((resolve, reject) => {
      enterprise.ready(() => {
        enterprise.execute(siteKey, { action }).then(resolve, reject);
      });
    });

    const timedOut = new Promise<never>((_, reject) => {
      timeout = setTimeout(
        () => reject(new Error('reCAPTCHA timed out')),
        EXECUTE_TIMEOUT_MS
      );
    });

    try {
      return await Promise.race([token, timedOut]);
    } finally {
      clearTimeout(timeout);
    }
  };
}
